import React, { useEffect, useState } from "react";
import { Facebook, Twitter, Instagram, Youtube, Linkedin, Phone, MessageCircle, Mail, MessageSquare, ClipboardCheck, MapPin, ShieldCheck, Server, Globe, Award, ExternalLink } from "lucide-react";
import { useModals } from "../contexts/ModalContext";
import { API } from "../api";
import Logo from "./Logo";

const SOCIAL_ICONS = { facebook: Facebook, twitter: Twitter, instagram: Instagram, youtube: Youtube, linkedin: Linkedin };

const FALLBACK = {
  footerAbout: "redwork.ch ist Ihre Schweizer Premium-Agentur für High-Performance Webhosting, Cloud-Infrastruktur, massgeschneidertes Webdesign, Software-Entwicklung und SEO.",
  footerAddress: "Bahnhofstrasse 1, 8001 Zürich, Schweiz",
  footerPhone: "+41 44 000 00 00",
  footerEmail: "info@redwork.ch",
  footerCopyright: "© 2026 redwork.ch – Alle Rechte vorbehalten. 100% Hosted in Switzerland.",
  footerSocial: { facebook: "", twitter: "", instagram: "", youtube: "", linkedin: "" },
  partners: [],
  contactWhatsapp: "",
};

// High-authority Swiss & tech backflow / trust anchors
const TRUST_BACKFLOW = [
  { label: "Swiss Hosting & Privacy Standard", badge: "100% Swiss Hosted", url: "https://www.edoeb.admin.ch", icon: ShieldCheck, desc: "Schweizer DSG / FADP Konform" },
  { label: "SWITCH .ch Partner Registry", badge: "Offizieller DNS Partner", url: "https://www.nic.ch", icon: Globe, desc: ".ch & .li Domain Management" },
  { label: "cPanel & WHM Enterprise", badge: "Gold Partner Architecture", url: "https://cpanel.net", icon: Server, desc: "High-Performance NVMe Hosting" },
  { label: "ISO 27001 Datacenter Zürich", badge: "Swiss Datacenter Security", url: "https://www.iso.org", icon: Award, desc: "Höchste Schweizer Rechenzentrums-Sicherheit" },
];

export default function Footer() {
  const { openQuote, openContact } = useModals();
  const [s, setS] = useState(FALLBACK);

  useEffect(() => {
    fetch(`${API}/site-settings`)
      .then((r) => r.json())
      .then((d) => setS({ ...FALLBACK, ...d, footerSocial: { ...FALLBACK.footerSocial, ...(d.footerSocial || {}) } }))
      .catch(() => {});
  }, []);

  const hostingLinks = [
    { name: "Webhosting Übersicht", path: "/hosting" },
    { name: "Webhosting Basic", path: "/hosting/webhosting/basic" },
    { name: "Webhosting Professional", path: "/hosting/webhosting/professional" },
    { name: "Webhosting Enterprise", path: "/hosting/webhosting/enterprise" },
    { name: "Domain Registrierung", path: "/domains" },
    { name: "Cloud Server & VPS", path: "/vps" },
  ];

  const serviceLinks = [
    { name: "Webdesign & UI/UX", path: "/services" },
    { name: "Software-Entwicklung", path: "/services" },
    { name: "Suchmaschinenoptimierung (SEO)", path: "/services" },
    { name: "E-Commerce & Online-Shops", path: "/services" },
    { name: "Website-Wartung & Support", path: "/services" },
    { name: "Kundenportal & Login", path: "/login" },
  ];

  return (
    <footer className="bg-[#0a0a0a] text-white pt-16 sm:pt-20 pb-8 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6">
        <div className="flex justify-center mb-8 sm:mb-10">
          <Logo size="lg" />
        </div>

        <div className="max-w-3xl mx-auto text-center text-[14px] sm:text-[15px] text-white/80 leading-relaxed">
          <p>{s.footerAbout}</p>
          <p className="mt-4 text-[#FFC107] font-semibold">12 Monate kostenloser Schweizer Premium-Support für alle unsere Hosting- und Agenturkunden inklusive!</p>
        </div>

        {/* Dynamic & Fallback Partners */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mt-8 sm:mt-10">
          {(s.partners || []).map((p) => (
            <div key={p} className="text-[9px] sm:text-[10px] tracking-wider font-bold text-white/80 border border-white/15 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded">{p}</div>
          ))}
        </div>

        {/* Backflow / Authority Trust Strip */}
        <div className="mt-12 pt-8 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TRUST_BACKFLOW.map((item, idx) => {
            const Icon = item.icon;
            return (
              <a
                key={idx}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group p-3.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.07] border border-white/10 hover:border-[#E63946]/40 transition-all flex items-start gap-3"
              >
                <div className="p-2 rounded-lg bg-red-500/10 text-[#E63946] group-hover:bg-[#E63946] group-hover:text-white transition-colors flex-shrink-0">
                  <Icon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-white/90 group-hover:text-white">
                    <span>{item.label}</span>
                    <ExternalLink size={10} className="text-white/40 group-hover:text-[#E63946] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="text-[10px] text-emerald-400 font-medium mt-0.5">{item.badge}</div>
                  <div className="text-[10px] text-white/50 truncate mt-0.5">{item.desc}</div>
                </div>
              </a>
            );
          })}
        </div>

        {/* Quick Navigation Sections for SEO Internal Linking */}
        <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-8 text-[13px]">
          <div>
            <div className="text-xs uppercase tracking-wider text-white/40 font-semibold mb-3 flex items-center gap-2">
              <Server size={14} className="text-[#E63946]" />
              <span>Schweizer NVMe Webhosting & Cloud</span>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {hostingLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.path}
                  className="text-white/70 hover:text-[#FFC107] transition-colors flex items-center gap-1.5"
                >
                  <span className="text-[#E63946]">›</span>
                  <span>{link.name}</span>
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-white/40 font-semibold mb-3 flex items-center gap-2">
              <Globe size={14} className="text-[#E63946]" />
              <span>Agentur & Services</span>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {serviceLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.path}
                  className="text-white/70 hover:text-[#FFC107] transition-colors flex items-center gap-1.5"
                >
                  <span className="text-[#E63946]">›</span>
                  <span>{link.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Info Bar */}
        <div className="mt-10 pt-6 border-t border-white/10 grid sm:grid-cols-3 gap-4 text-sm">
          {s.footerAddress && (
            <a href={`https://maps.google.com/?q=${encodeURIComponent(s.footerAddress)}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-white/75 hover:text-white">
              <MapPin size={15} className="text-[#E63946] flex-shrink-0" />
              <span className="truncate">{s.footerAddress}</span>
            </a>
          )}
          {s.footerPhone && (
            <a href={`tel:${s.footerPhone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-white/75 hover:text-white">
              <Phone size={15} className="text-[#22C55E] flex-shrink-0" />
              <span>{s.footerPhone}</span>
            </a>
          )}
          {s.footerEmail && (
            <a href={`mailto:${s.footerEmail}`} className="flex items-center gap-2 text-white/75 hover:text-white">
              <Mail size={15} className="text-[#1E88E5] flex-shrink-0" />
              <span>{s.footerEmail}</span>
            </a>
          )}
        </div>

        {/* Bottom Social & Action Controls */}
        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="flex gap-3">
            {Object.entries(s.footerSocial || {}).filter(([, url]) => url).map(([name, url]) => {
              const Icon = SOCIAL_ICONS[name] || Facebook;
              return (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={name}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#1E88E5] flex items-center justify-center transition-colors"
                >
                  <Icon size={16} />
                </a>
              );
            })}
          </div>

          <div className="text-center text-xs text-white/60">
            {s.footerCopyright}
          </div>

          <div className="flex gap-2 sm:gap-3 flex-wrap justify-center">
            {s.footerPhone && <a href={`tel:${s.footerPhone.replace(/\s/g, "")}`} className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#E63946] flex items-center justify-center transition-colors" aria-label="Telefon"><Phone size={16} /></a>}
            {s.contactWhatsapp && <a href={`https://wa.me/${s.contactWhatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#22C55E] flex items-center justify-center transition-colors" aria-label="WhatsApp"><MessageCircle size={16} /></a>}
            {s.footerEmail && <a href={`mailto:${s.footerEmail}`} className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#1E88E5] flex items-center justify-center transition-colors" aria-label="E-Mail"><Mail size={16} /></a>}
            <button onClick={openContact} className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#E63946] flex items-center justify-center transition-colors" aria-label="Nachricht"><MessageSquare size={16} /></button>
            <button onClick={() => openQuote()} className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#FFC107] hover:text-black flex items-center justify-center transition-colors" aria-label="Angebot"><ClipboardCheck size={16} /></button>
          </div>
        </div>
      </div>
    </footer>
  );
}
